// src/components/settings/ClientBankForm.jsx

import React, { useEffect, useState } from "react";
import {
  Edit2,
  Trash2,
  Plus,
  Landmark,
  Star,
  X,
} from "lucide-react";

import {
  useClientInfo,
  axiosInstance,
} from "../../utils/commonImports";


// ============================================================
// EMPTY FORM
// ============================================================

const emptyForm = {
  bank_name: "",
  branch: "",
  account_number: "",
  ifsc_code: "",
  swift_code: "",
  account_holder_name: "",
  is_primary: false,
};


// ============================================================
// COMMON STYLES
// ============================================================

const inputClass =
  "w-full h-9 px-3 border border-gray-300 rounded-md " +
  "text-sm text-gray-700 bg-white " +
  "focus:outline-none focus:ring-1 focus:ring-blue-500 " +
  "focus:border-blue-500";

const labelClass =
  "block text-xs font-medium text-gray-600 mb-1";


// ============================================================
// BANK API URL
//
// Works whether axiosInstance baseURL is:
//   http://localhost:5000
//   http://localhost:5000/api
//   /api
//   or empty
// ============================================================

const getBankApiUrl = (path = "") => {
  const baseUrl = String(
    axiosInstance?.defaults?.baseURL || ""
  ).replace(/\/+$/, "");

  if (!baseUrl) {
    return `/api/client-banks${path}`;
  }

  if (baseUrl.endsWith("/api")) {
    return `${baseUrl}/client-banks${path}`;
  }

  return `${baseUrl}/api/client-banks${path}`;
};


// ============================================================
// SECTION TITLE
// ============================================================

const SectionTitle = ({ number, title }) => (
  <div className="flex items-center gap-2 mb-3 mt-1">
    <span
      className="text-[10px] font-semibold text-blue-600
                 bg-blue-50 px-2 py-1 rounded"
    >
      {number}
    </span>

    <h3 className="text-sm font-semibold text-gray-800">
      {title}
    </h3>

    <div className="flex-1 border-t border-gray-200" />
  </div>
);


// ============================================================
// FIELD
// ============================================================

const Field = ({ label, required, children }) => (
  <div>
    <label className={labelClass}>
      {label}

      {required && (
        <span className="text-red-500 ml-1">*</span>
      )}
    </label>

    {children}
  </div>
);


// ============================================================
// MASK ACCOUNT NUMBER
// ============================================================

const maskAccountNumber = (number) => {
  if (!number) return "-";

  const value = String(number);

  if (value.length <= 4) {
    return value;
  }

  return `**** ${value.slice(-4)}`;
};


// ============================================================
// COMPONENT
// ============================================================

const ClientBankForm = () => {
  const { clientId } = useClientInfo();

  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });


  // ==========================================================
  // SHOW INLINE MESSAGE
  // ==========================================================

  const showAlert = (
    message,
    severity = "success"
  ) => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };


  const closeAlert = () => {
    setAlert((prev) => ({
      ...prev,
      open: false,
    }));
  };


  // ==========================================================
  // ERROR MESSAGE
  // ==========================================================

  const getErrorMessage = (error, fallback) => {
    return (
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  };


  // ==========================================================
  // LOAD BANK ACCOUNTS
  // ==========================================================

  const fetchBanks = async () => {
    if (!clientId) {
      console.warn(
        "⚠️ ClientBankForm: clientId not available"
      );
      return;
    }

    try {
      setLoading(true);

      const url = getBankApiUrl(
        `/${clientId}`
      );

      console.log(
        "📥 Loading bank accounts:",
        url
      );

      const response = await axiosInstance.get(url);

      console.log(
        "📤 BANK API RESPONSE:",
        response.data
      );

      const rows = Array.isArray(response.data)
        ? response.data
        : [];

      setBanks(rows);
    } catch (error) {
      console.error(
        "❌ Error loading bank accounts:",
        error
      );

      console.error(
        "❌ BANK STATUS:",
        error?.response?.status
      );

      console.error(
        "❌ BANK RESPONSE:",
        error?.response?.data
      );

      console.error(
        "❌ BANK URL:",
        error?.config?.url
      );

      showAlert(
        getErrorMessage(
          error,
          "Unable to load bank accounts"
        ),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchBanks();
  }, [clientId]);


  // ==========================================================
  // AUTO HIDE ALERT
  // ==========================================================

  useEffect(() => {
    if (!alert.open) return;

    const timer = setTimeout(() => {
      setAlert((prev) => ({
        ...prev,
        open: false,
      }));
    }, 4000);

    return () => clearTimeout(timer);
  }, [alert.open]);


  // ==========================================================
  // HANDLE INPUT
  // ==========================================================

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };


  // ==========================================================
  // ADD BANK
  // ==========================================================

  const handleAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    closeAlert();
    setShowForm(true);
  };


  // ==========================================================
  // EDIT BANK
  // ==========================================================

  const handleEdit = (bank) => {
    setEditingId(bank.bank_id);

    setForm({
      bank_name: bank.bank_name || "",
      branch: bank.branch || "",
      account_number: bank.account_number || "",
      ifsc_code: bank.ifsc_code || "",
      swift_code: bank.swift_code || "",
      account_holder_name:
        bank.account_holder_name || "",
      is_primary: Boolean(bank.is_primary),
    });

    closeAlert();
    setShowForm(true);
  };


  // ==========================================================
  // CANCEL
  // ==========================================================

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };


  // ==========================================================
  // SAVE
  // ==========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clientId) {
      showAlert(
        "Client information is not available.",
        "error"
      );
      return;
    }

    if (!form.bank_name.trim()) {
      showAlert(
        "Please enter bank name.",
        "error"
      );
      return;
    }

    if (!form.account_number.trim()) {
      showAlert(
        "Please enter account number.",
        "error"
      );
      return;
    }

    try {
      setSaving(true);
      closeAlert();

      const payload = {
        bank_name: form.bank_name.trim(),

        branch:
          form.branch.trim() || null,

        account_number:
          form.account_number.trim(),

        ifsc_code:
          form.ifsc_code.trim().toUpperCase() || null,

        swift_code:
          form.swift_code.trim().toUpperCase() || null,

        account_holder_name:
          form.account_holder_name.trim() || null,

        is_primary:
          Boolean(form.is_primary),
      };


      // ======================================================
      // UPDATE
      // ======================================================

      if (editingId) {
        const url = getBankApiUrl(
          `/${clientId}/${editingId}`
        );

        console.log(
          "✏️ Updating bank account:",
          url,
          payload
        );

        await axiosInstance.put(
          url,
          payload
        );

        showAlert(
          "Bank account updated successfully.",
          "success"
        );
      }


      // ======================================================
      // ADD
      // ======================================================

      else {
        const url = getBankApiUrl(
          `/${clientId}`
        );

        console.log(
          "➕ Adding bank account:",
          url,
          payload
        );

        await axiosInstance.post(
          url,
          payload
        );

        showAlert(
          "Bank account added successfully.",
          "success"
        );
      }


      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyForm });

      await fetchBanks();

    } catch (error) {
      console.error(
        "❌ Bank save error:",
        error
      );

      console.error(
        "❌ BANK SAVE STATUS:",
        error?.response?.status
      );

      console.error(
        "❌ BANK SAVE RESPONSE:",
        error?.response?.data
      );

      console.error(
        "❌ BANK SAVE URL:",
        error?.config?.url
      );

      showAlert(
        getErrorMessage(
          error,
          "Unable to save bank account."
        ),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };


  // ==========================================================
  // DELETE
  // ==========================================================

  const handleDelete = async (bankId) => {
    if (!clientId) return;

    const bank = banks.find(
      (item) => item.bank_id === bankId
    );

    const bankName =
      bank?.bank_name ||
      "this bank account";

    const confirmed = window.confirm(
      `Are you sure you want to delete ${bankName}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      const url = getBankApiUrl(
        `/${clientId}/${bankId}`
      );

      console.log(
        "🗑️ Deleting bank account:",
        url
      );

      await axiosInstance.delete(url);

      showAlert(
        "Bank account deleted successfully.",
        "success"
      );

      await fetchBanks();

    } catch (error) {
      console.error(
        "❌ Bank delete error:",
        error
      );

      console.error(
        "❌ BANK DELETE STATUS:",
        error?.response?.status
      );

      console.error(
        "❌ BANK DELETE RESPONSE:",
        error?.response?.data
      );

      console.error(
        "❌ BANK DELETE URL:",
        error?.config?.url
      );

      showAlert(
        getErrorMessage(
          error,
          "Unable to delete bank account."
        ),
        "error"
      );
    }
  };


  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="w-full max-w-6xl mx-auto">

      {/* ======================================================
          ALERT
      ======================================================= */}

      {alert.open && (
        <div
          className={`mb-3 px-3 py-2 rounded-md
            border text-xs flex items-center
            justify-between ${
              alert.severity === "error"
                ? "bg-red-50 border-red-200 text-red-700"
                : "bg-green-50 border-green-200 text-green-700"
            }`}
          role="alert"
        >
          <span>{alert.message}</span>

          <button
            type="button"
            onClick={closeAlert}
            className="ml-3 opacity-60
                       hover:opacity-100"
            aria-label="Close message"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}


      {/* ======================================================
          HEADER
      ======================================================= */}

      <div className="flex items-center justify-between mb-4">

        <div>
          <h2
            className="text-lg font-semibold
                       text-gray-800"
          >
            Bank Accounts
          </h2>

          <p
            className="text-xs text-gray-500
                       mt-0.5"
          >
            Manage your organisation's bank accounts
          </p>
        </div>


        {!showForm && (
          <button
            type="button"
            onClick={handleAdd}
            className="h-9 px-4 bg-blue-600
                       hover:bg-blue-700
                       text-white text-sm
                       font-medium rounded-md
                       flex items-center gap-1.5
                       transition"
          >
            <Plus className="w-4 h-4" />
            Add Bank Account
          </button>
        )}

      </div>


      {/* ======================================================
          ADD / EDIT FORM
      ======================================================= */}

      {showForm ? (
        <form
          onSubmit={handleSubmit}
          className="bg-white border
                     border-gray-200 rounded-lg
                     overflow-hidden"
        >

          <div
            className="px-4 py-3
                       border-b border-gray-200"
          >
            <h3
              className="text-sm font-semibold
                         text-gray-800"
            >
              {editingId
                ? "Edit Bank Account"
                : "Add Bank Account"}
            </h3>

            <p
              className="text-[11px]
                         text-gray-500 mt-0.5"
            >
              Enter the bank account details
            </p>
          </div>


          <div className="p-4 space-y-4">

            {/* =================================================
                SECTION 01
            ================================================== */}

            <SectionTitle
              number="01"
              title="Bank Information"
            />

            <div
              className="grid grid-cols-1
                         md:grid-cols-2
                         gap-x-5 gap-y-3"
            >

              <Field
                label="Bank Name"
                required
              >
                <input
                  type="text"
                  name="bank_name"
                  value={form.bank_name}
                  onChange={handleChange}
                  placeholder="e.g. HDFC Bank"
                  className={inputClass}
                  autoComplete="off"
                />
              </Field>


              <Field label="Branch">
                <input
                  type="text"
                  name="branch"
                  value={form.branch}
                  onChange={handleChange}
                  placeholder="e.g. Koramangala"
                  className={inputClass}
                  autoComplete="off"
                />
              </Field>


              <Field
                label="Account Number"
                required
              >
                <input
                  type="text"
                  name="account_number"
                  value={form.account_number}
                  onChange={handleChange}
                  placeholder="Enter account number"
                  className={inputClass}
                  autoComplete="off"
                />
              </Field>


              <Field label="Account Holder Name">
                <input
                  type="text"
                  name="account_holder_name"
                  value={form.account_holder_name}
                  onChange={handleChange}
                  placeholder="Account holder name"
                  className={inputClass}
                  autoComplete="off"
                />
              </Field>

            </div>


            {/* =================================================
                SECTION 02
            ================================================== */}

            <SectionTitle
              number="02"
              title="Bank Identification"
            />

            <div
              className="grid grid-cols-1
                         md:grid-cols-2
                         gap-x-5 gap-y-3"
            >

              <Field label="IFSC Code">
                <input
                  type="text"
                  name="ifsc_code"
                  value={form.ifsc_code}
                  onChange={handleChange}
                  placeholder="e.g. HDFC0001234"
                  className={inputClass}
                  autoComplete="off"
                  style={{
                    textTransform: "uppercase",
                  }}
                />
              </Field>


              <Field label="SWIFT Code">
                <input
                  type="text"
                  name="swift_code"
                  value={form.swift_code}
                  onChange={handleChange}
                  placeholder="e.g. HDFCINBB"
                  className={inputClass}
                  autoComplete="off"
                  style={{
                    textTransform: "uppercase",
                  }}
                />
              </Field>

            </div>


            {/* =================================================
                SECTION 03
            ================================================== */}

            <SectionTitle
              number="03"
              title="Account Settings"
            />

            <div className="grid grid-cols-1 md:grid-cols-2">

              <div
                className="flex items-center
                           justify-between
                           border border-gray-200
                           rounded-md px-3 h-11"
              >

                <div>
                  <p
                    className="text-xs font-medium
                               text-gray-700"
                  >
                    Primary Bank Account
                  </p>

                  <p
                    className="text-[10px]
                               text-gray-400"
                  >
                    Use this as the default account
                  </p>
                </div>


                <label
                  className="relative inline-flex
                             items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    name="is_primary"
                    checked={form.is_primary}
                    onChange={handleChange}
                    className="sr-only peer"
                  />

                  <div
                    className="
                      w-9 h-5
                      bg-gray-300
                      rounded-full
                      peer-checked:bg-blue-600
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:rounded-full
                      after:h-4
                      after:w-4
                      after:transition-all
                      peer-checked:after:translate-x-4
                    "
                  />
                </label>

              </div>

            </div>

          </div>


          {/* ==================================================
              FOOTER
          =================================================== */}

          <div
            className="px-4 py-3
                       border-t border-gray-200
                       bg-gray-50
                       flex justify-end gap-2"
          >

            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="h-9 px-4
                         border border-gray-300
                         bg-white text-gray-700
                         text-sm rounded-md
                         hover:bg-gray-50
                         disabled:opacity-50"
            >
              Cancel
            </button>


            <button
              type="submit"
              disabled={saving}
              className="h-9 px-5
                         bg-blue-600
                         hover:bg-blue-700
                         text-white text-sm
                         font-medium rounded-md
                         disabled:opacity-60"
            >
              {saving
                ? "Saving..."
                : editingId
                  ? "Update Account"
                  : "Save Account"}
            </button>

          </div>

        </form>
      ) : (

        /* =====================================================
           BANK TABLE
        ====================================================== */

        <div
          className="bg-white border
                     border-gray-200
                     rounded-lg overflow-hidden"
        >

          <div
            className="px-4 py-3
                       border-b border-gray-200
                       flex items-center
                       justify-between"
          >

            <div className="flex items-center gap-2">

              <div
                className="w-8 h-8 rounded-md
                           bg-blue-50
                           flex items-center
                           justify-center"
              >
                <Landmark
                  className="w-4 h-4
                             text-blue-600"
                />
              </div>

              <div>
                <h3
                  className="text-sm font-semibold
                             text-gray-800"
                >
                  Bank Accounts
                </h3>

                <p
                  className="text-[11px]
                             text-gray-500"
                >
                  {banks.length} account
                  {banks.length !== 1 ? "s" : ""}
                </p>
              </div>

            </div>

          </div>


          {/* ==================================================
              LOADING
          =================================================== */}

          {loading ? (

            <div
              className="py-12 text-center
                         text-sm text-gray-500"
            >
              Loading bank accounts...
            </div>

          ) : banks.length === 0 ? (

            /* =================================================
               EMPTY
            ================================================== */

            <div className="py-12 text-center">

              <Landmark
                className="w-10 h-10
                           text-gray-300
                           mx-auto mb-3"
              />

              <p
                className="text-sm font-medium
                           text-gray-700"
              >
                No bank accounts added
              </p>

              <p
                className="text-xs text-gray-500
                           mt-1"
              >
                Add your organisation's first
                bank account.
              </p>

              <button
                type="button"
                onClick={handleAdd}
                className="mt-4 h-8 px-4
                           bg-blue-600
                           hover:bg-blue-700
                           text-white text-xs
                           rounded-md
                           inline-flex items-center
                           gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Bank Account
              </button>

            </div>

          ) : (

            /* =================================================
               TABLE
            ================================================== */

            <div className="overflow-x-auto">

              <table className="w-full">

                <thead>
                  <tr
                    className="bg-gray-50
                               border-b border-gray-200"
                  >

                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">
                      Bank Name
                    </th>

                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">
                      Branch
                    </th>

                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">
                      Account Number
                    </th>

                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">
                      IFSC
                    </th>

                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">
                      Account Holder
                    </th>

                    <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">
                      Primary
                    </th>

                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">
                      Action
                    </th>

                  </tr>
                </thead>


                <tbody>

                  {banks.map((bank) => (

                    <tr
                      key={bank.bank_id}
                      className="border-b
                                 border-gray-100
                                 hover:bg-gray-50
                                 transition"
                    >

                      <td className="px-4 py-3">
                        <div
                          className="text-sm font-medium
                                     text-gray-800"
                        >
                          {bank.bank_name || "-"}
                        </div>
                      </td>


                      <td
                        className="px-4 py-3 text-xs
                                   text-gray-600"
                      >
                        {bank.branch || "-"}
                      </td>


                      <td className="px-4 py-3">
                        <span
                          className="font-mono text-xs
                                     text-gray-700"
                        >
                          {maskAccountNumber(
                            bank.account_number
                          )}
                        </span>
                      </td>


                      <td
                        className="px-4 py-3 text-xs
                                   text-gray-600"
                      >
                        {bank.ifsc_code || "-"}
                      </td>


                      <td
                        className="px-4 py-3 text-xs
                                   text-gray-600"
                      >
                        {bank.account_holder_name || "-"}
                      </td>


                      <td
                        className="px-4 py-3 text-center"
                      >
                        {Boolean(bank.is_primary) ? (

                          <span
                            className="
                              inline-flex items-center
                              gap-1 px-2 py-1 rounded
                              text-[10px] font-medium
                              bg-blue-50 text-blue-700
                            "
                          >
                            <Star
                              className="w-3 h-3
                                         fill-current"
                            />
                            Primary
                          </span>

                        ) : (

                          <span
                            className="text-gray-400
                                       text-xs"
                          >
                            —
                          </span>

                        )}
                      </td>


                      <td className="px-4 py-3">

                        <div
                          className="flex justify-end
                                     gap-1.5"
                        >

                          <button
                            type="button"
                            onClick={() =>
                              handleEdit(bank)
                            }
                            title="Edit"
                            className="
                              w-8 h-8 flex items-center
                              justify-center rounded-md
                              border border-blue-200
                              text-blue-600
                              hover:bg-blue-50
                            "
                          >
                            <Edit2
                              className="w-3.5 h-3.5"
                            />
                          </button>


                          <button
                            type="button"
                            onClick={() =>
                              handleDelete(
                                bank.bank_id
                              )
                            }
                            title="Delete"
                            className="
                              w-8 h-8 flex items-center
                              justify-center rounded-md
                              border border-red-200
                              text-red-600
                              hover:bg-red-50
                            "
                          >
                            <Trash2
                              className="w-3.5 h-3.5"
                            />
                          </button>

                        </div>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          )}

        </div>
      )}

    </div>
  );
};


export default ClientBankForm;
